/**
 * Recently Viewed controller (CU-86d46vcvq CP-03).
 *
 * Walks the saved history in order, checks each eligible entry with the card
 * filter's purchasability rule (window.CardVariantFilter) and paints the first
 * few buyable products into every Recently Viewed section. It repeats the choice
 * whenever the delivery preference changes and when the theme editor reloads a
 * section. The section wrapper carries section--unavailable-hidden until the
 * first paint of at least one product, and again whenever nothing is painted.
 *
 * Only the children of the section's slider list, the counter text and the
 * wrapper's class are ever changed.
 */
(function () {
  'use strict';

  if (window.__recentlyViewedControllerLoaded) return;
  window.__recentlyViewedControllerLoaded = true;

  var HISTORY_KEY = 'recentlyViewedProducts';
  var BATCH_SIZE = 6;
  var MAX_CANDIDATES = 24;
  var HIDDEN_SECTION_CLASS = 'section--unavailable-hidden';

  var CONFIG_SELECTOR = 'script[data-recently-viewed-config]';

  var controllers = [];
  var knownConfigs = new WeakSet();
  var inFlight = new Map();
  var apiMissingLogged = false;

  /**
   * Parse the stored history: comma separated handles, newest first.
   * @param {*} raw - the stored value
   * @returns {Array} handles in order, without blanks or repeats
   */
  function readHistory(raw) {
    if (typeof raw !== 'string') return [];

    var handles = [];
    raw.split(',').forEach(function (part) {
      var handle = part.trim();
      if (handle === '' || handles.indexOf(handle) !== -1) return;
      handles.push(handle);
    });
    return handles;
  }

  /**
   * Drop excluded handles and keep only the first maxCandidates.
   * @param {Array} history - handles in order
   * @param {Array} hiddenHandles - handles the admin excluded
   * @param {number} maxCandidates - the evaluation limit
   * @returns {Array} the handles to evaluate
   */
  function eligibleHistory(history, hiddenHandles, maxCandidates) {
    var hidden = hiddenHandles || [];
    return history
      .filter(function (handle) {
        return hidden.indexOf(handle) === -1;
      })
      .slice(0, maxCandidates);
  }

  /**
   * Pick the buyable handles in history order, never past the first candidate
   * that has not been checked yet, so history order is never broken.
   * @param {Array} candidates - handles in order
   * @param {Map} cache - handle to entry
   * @param {Object|null} preference - the delivery preference
   * @param {Object} api - object with isPurchasable(availability, preference)
   * @param {number} limit - most handles to select
   * @returns {Object} { handles: Array, unchecked: Array }
   */
  function selectHandles(candidates, cache, preference, api, limit) {
    var handles = [];
    var unchecked = [];
    var blocked = false;

    candidates.forEach(function (handle) {
      var entry = cache.get(handle);
      if (!entry) {
        unchecked.push(handle);
        blocked = true;
        return;
      }
      if (blocked || handles.length >= limit) return;
      if (entry.status === 'ok' && !entry.hidden && api.isPurchasable(entry.availability, preference)) {
        handles.push(handle);
      }
    });

    return { handles: handles, unchecked: unchecked };
  }

  /**
   * Read a card view's text: whether the card is hidden and its availability JSON.
   * @param {string} text - the card view response body
   * @returns {Object|null} { hidden, availability } or null when there is no card
   */
  function parseCardText(text) {
    var doc = new DOMParser().parseFromString(text, 'text/html');
    var card = doc.querySelector('card-product');
    if (!card) return null;

    var availability = null;
    var script = card.querySelector('script.card-variant-availability');
    if (script) {
      try {
        availability = JSON.parse(script.textContent);
      } catch (e) {
        availability = null;
      }
    }

    return { hidden: card.classList.contains('card--hidden'), availability: availability };
  }

  /**
   * Fetch one product's card view or availability view as a cache entry.
   * @param {string} handle - the product handle
   * @param {string} view - card-view or rv-availability
   * @returns {Promise<Object>} the entry, never rejects
   */
  function fetchEntry(handle, view) {
    var key = view + ':' + handle;
    if (inFlight.has(key)) return inFlight.get(key);

    var promise = fetch('/products/' + encodeURIComponent(handle) + '?view=' + view)
      .then(function (response) {
        if (response.status === 404) return { status: 'missing' };
        if (!response.ok) return { status: 'error' };

        return response.text().then(function (text) {
          if (view === 'card-view') {
            var parsed = parseCardText(text);
            if (!parsed) return { status: 'error' };
            return { status: 'ok', hidden: parsed.hidden, availability: parsed.availability, cardHtml: text };
          }
          var json = JSON.parse(text);
          return { status: 'ok', hidden: json.hidden === true, availability: json.variants || null, cardHtml: null };
        });
      })
      .catch(function () {
        return { status: 'error' };
      })
      .then(function (entry) {
        inFlight.delete(key);
        return entry;
      });

    inFlight.set(key, promise);
    return promise;
  }

  /**
   * Paint the selected handles into the section and set its visible state.
   * @param {Object} controller - the section controller
   * @param {Array} handles - handles to paint, in order
   */
  function paint(controller, handles) {
    var section = controller.section;
    var list = section.querySelector('ul[id^="Slider-"]');

    if (list) {
      while (list.firstChild) list.removeChild(list.firstChild);
      handles.forEach(function (handle, index) {
        var slide = document.createElement('li');
        slide.id = 'Slide-recently-viewed-products-' + (index + 1);
        slide.className = 'grid__item slider__slide';
        slide.innerHTML = controller.cache.get(handle).cardHtml;
        list.appendChild(slide);
      });
    }

    var counter = section.querySelector('.recent-product--count');
    if (counter) counter.textContent = String(handles.length);

    if (handles.length === 0) section.classList.add(HIDDEN_SECTION_CLASS);
    else section.classList.remove(HIDDEN_SECTION_CLASS);

    var slider = section.querySelector('slider-component');
    if (slider && slider.slider && slider.prevButton && slider.nextButton && typeof slider.resetPages === 'function') {
      slider.resetPages();
    }

    controller.cache.forEach(function (entry, handle) {
      if (entry.status === 'error') controller.cache.delete(handle);
    });
  }

  /**
   * Evaluate the history for the current preference and paint the result.
   * A newer render makes an older one stop before it paints.
   * @param {Object} controller - the section controller
   * @returns {Promise<void>}
   */
  async function render(controller) {
    controller.token += 1;
    var token = controller.token;
    var cache = controller.cache;
    var limit = controller.config.limit || BATCH_SIZE;

    var history = [];
    try {
      history = readHistory(localStorage.getItem(HISTORY_KEY));
    } catch (e) {
      history = [];
    }
    var candidates = eligibleHistory(history, controller.config.hiddenHandles, MAX_CANDIDATES);
    if (candidates.length === 0) {
      paint(controller, []);
      return;
    }

    var api = window.CardVariantFilter;
    var preference = null;
    var cardOnly = false;
    if (api) {
      preference = api.getDeliveryPreference();
    } else {
      if (!apiMissingLogged) {
        apiMissingLogged = true;
        console.error('[recently-viewed] card filter API missing');
      }
      api = {
        isPurchasable: function () {
          return true;
        }
      };
      cardOnly = true;
    }

    var selected = [];
    for (;;) {
      var pick = selectHandles(candidates, cache, preference, api, limit);
      var needCard = pick.handles.filter(function (handle) {
        return !cache.get(handle).cardHtml;
      });

      if (pick.handles.length >= limit || pick.unchecked.length === 0) {
        if (needCard.length === 0) {
          selected = pick.handles;
          break;
        }
        var cards = await Promise.all(
          needCard.map(function (handle) {
            return fetchEntry(handle, 'card-view');
          })
        );
        if (controller.token !== token) return;
        needCard.forEach(function (handle, index) {
          cache.set(handle, cards[index]);
        });
        continue;
      }

      var batch = pick.unchecked.slice(0, BATCH_SIZE);
      var view = cardOnly || cache.size === 0 ? 'card-view' : 'rv-availability';
      var entries = await Promise.all(
        batch.map(function (handle) {
          return fetchEntry(handle, view);
        })
      );
      if (controller.token !== token) return;
      batch.forEach(function (handle, index) {
        cache.set(handle, entries[index]);
      });
    }

    paint(controller, selected);
  }

  function startRender(controller) {
    render(controller).catch(function (error) {
      console.error('[recently-viewed] render failed', error);
    });
  }

  /**
   * Create and render a controller for every config script that has none yet.
   */
  function createControllers() {
    controllers = controllers.filter(function (controller) {
      return controller.section.isConnected !== false && controller.script.isConnected !== false;
    });

    var scripts = document.querySelectorAll(CONFIG_SELECTOR);
    Array.prototype.forEach.call(scripts, function (script) {
      if (knownConfigs.has(script)) return;

      var config;
      try {
        config = JSON.parse(script.textContent);
      } catch (e) {
        return;
      }
      var section = script.closest('.shopify-section');
      if (!config || !section) return;

      knownConfigs.add(script);
      var controller = { script: script, section: section, config: config, cache: new Map(), token: 0 };
      controllers.push(controller);
      startRender(controller);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', createControllers);
  } else {
    createControllers();
  }

  document.addEventListener('deliveryPreference:changed', function () {
    controllers.forEach(startRender);
  });

  document.addEventListener('shopify:section:load', createControllers);
})();
